import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useGetProfile, customFetch } from "@workspace/api-client-react";
// SSOT: column names + descriptions for the school records CSV export
// (Task #136). Server header row is built from the same constant in
// `artifacts/api-server/src/routes/school.ts` so the two can never drift.
// IMPORTANT — import the browser-safe subpath, NOT `@workspace/db` root.
// The root entrypoint eagerly constructs a `pg.Pool` and throws when
// `DATABASE_URL` is unset; pulling it into the client bundle would
// break app boot in the browser.
import { SCHOOL_RECORDS_CSV_COLUMNS } from "@workspace/db/school-csv-columns";
import {
  Users, FileText, AlertTriangle, TrendingUp,
  ChevronRight, Building2, BarChart3,
  GraduationCap, Loader2, Download, CheckCircle2, XCircle,
  Layers, Plus, Pencil, Trash2, X, Check,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { format } from "date-fns";

const TYPE_COLORS: Record<string, string> = {
  nccd: "#8b5cf6",
  contact: "#3b82f6",
  behaviour: "#ef4444",
  general: "#9ca3af",
};
const TYPE_LABELS: Record<string, string> = {
  nccd: "NCCD",
  contact: "Contact",
  behaviour: "Behaviour",
  general: "General",
};

const GROUP_TYPES = [
  { value: "stage", label: "Stage / Year Level" },
  { value: "campus", label: "Campus" },
  { value: "department", label: "Department" },
  { value: "custom", label: "Custom" },
];

const PALETTE = [
  "#1cb08a", "#378add", "#ef9f27", "#76d0b9",
  "#87b9ea", "#8b5cf6", "#f97316", "#64748b",
  "#06b6d4", "#10b981",
];

interface SchoolOverviewResponse {
  schoolName: string;
  schoolCode: string;
  summary: {
    totalRecords: number;
    totalStudents: number;
    totalTeachers: number;
    openFollowUps: number;
    enteredCount: number;
    notEnteredCount: number;
    recordsByType?: Record<string, number>;
  };
  weeklyActivity: Array<{ date: string; count: number }>;
  perTeacher: Array<{
    userId: string;
    name: string | null;
    teachingLevel?: string | null;
    totalRecords: number;
    totalStudents: number;
    openFollowUps: number;
    notEnteredCount: number;
    lastActivity?: string | null;
  }>;
}

interface TeacherDetailResponse {
  teacher: { name: string | null; teachingLevel?: string | null };
  totalRecords: number;
  totalStudents: number;
  openFollowUps: number;
  enteredCount: number;
  notEnteredCount: number;
  recordsByType?: Record<string, number>;
  topStudents: Array<{ studentId: number; studentName: string; count: number }>;
  recentRecords: Array<{
    id: number;
    type: string;
    status?: string | null;
    studentNames?: string[];
    date: string;
  }>;
}

interface SchoolGroup {
  id: number;
  name: string;
  groupType: string;
  colour?: string | null;
  memberUserIds: string[];
}

async function fetchSchoolOverview() {
  return customFetch<SchoolOverviewResponse>("/api/school/overview", { credentials: "include" });
}
async function fetchTeacherDetail(teacherUserId: string) {
  return customFetch<TeacherDetailResponse>(`/api/school/teacher/${teacherUserId}`, { credentials: "include" });
}
async function fetchGroups() {
  return customFetch<SchoolGroup[]>("/api/school/groups", { credentials: "include" });
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

const CSV_COLUMNS = SCHOOL_RECORDS_CSV_COLUMNS;

function ExportCsvButton() {
  const [open, setOpen] = useState(false);
  const startDownload = async () => {
    const resp = await fetch("/api/school/export/csv", { credentials: "include" });
    if (!resp.ok) return;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "school-export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>What's in the school CSV</DialogTitle>
          <DialogDescription>
            Here's what each row in the download will contain — one row per record across all teachers in your school.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">Columns</p>
            <ul className="space-y-1.5">
              {CSV_COLUMNS.map((c) => (
                <li key={c.name} className="text-xs text-gray-700 flex gap-2">
                  <span className="font-mono font-semibold text-gray-900 shrink-0">{c.name}</span>
                  <span className="text-gray-500">— {c.desc}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-800 mb-1">Record description and notes are intentionally excluded</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              For privacy, the body of each record (description, shorthand notes, and any freeform fields) is left out of this export. If a teacher needs the full text of one of their own records, they can export it themselves from their own export page.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" className="gap-1.5" onClick={startDownload}>
            <Download className="h-3.5 w-3.5" /> Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "text-primary" }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2.5 rounded-xl bg-gray-50 ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TeacherRow({ teacher, onSelect }: { teacher: any; onSelect: () => void }) {
  const activityLevel =
    teacher.recordsThisWeek >= 5 ? "high" :
    teacher.recordsThisWeek >= 1 ? "medium" : "low";
  const activityBadge = {
    high: <Badge className="bg-green-100 text-green-700 border-0 text-xs">Active this week</Badge>,
    medium: <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Some activity</Badge>,
    low: <Badge className="bg-gray-100 text-gray-500 border-0 text-xs">No activity this week</Badge>,
  }[activityLevel];
  const entryPct = teacher.totalRecords > 0
    ? Math.round((teacher.enteredCount / teacher.totalRecords) * 100) : null;

  return (
    <button
      onClick={onSelect}
      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl transition-colors text-left group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-sm font-semibold text-primary">{teacher.name?.charAt(0)?.toUpperCase() ?? "?"}</span>
        </div>
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">{teacher.name}</p>
          {teacher.email && (
            <p
              className="text-xs text-gray-500 truncate"
              data-testid={`text-email-${teacher.userId}`}
            >
              {teacher.email}
            </p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {teacher.teachingLevel && <span className="text-xs text-gray-400">{teacher.teachingLevel}</span>}
            {activityBadge}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-5 ml-4 shrink-0">
        <div className="text-center hidden sm:block">
          <p className="text-sm font-semibold text-gray-900">{teacher.totalRecords}</p>
          <p className="text-xs text-gray-400">Records</p>
        </div>
        <div className="hidden sm:block w-28">
          {entryPct !== null ? (
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-gray-400">In system</span>
                <span className={`text-xs font-semibold ${entryPct < 50 ? "text-red-500" : entryPct < 80 ? "text-amber-500" : "text-green-600"}`}>{entryPct}%</span>
              </div>
              <Progress value={entryPct} className="h-1.5" />
              {teacher.notEnteredCount > 0 && <p className="text-xs text-red-500 mt-0.5">{teacher.notEnteredCount} pending</p>}
            </div>
          ) : <p className="text-xs text-gray-300 text-center">No records</p>}
        </div>
        {teacher.openFollowUps > 0 && (
          <div className="text-center hidden sm:block">
            <p className="text-sm font-semibold text-red-600">{teacher.openFollowUps}</p>
            <p className="text-xs text-gray-400">Follow-ups</p>
          </div>
        )}
        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
      </div>
    </button>
  );
}

// ─── Teacher Detail Panel ─────────────────────────────────────────────────────

function TeacherDetailPanel({ teacherUserId, onBack }: { teacherUserId: string; onBack: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["school", "teacher", teacherUserId],
    queryFn: () => fetchTeacherDetail(teacherUserId),
  });

  if (isLoading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (error || !data) return <div className="py-8 text-center text-gray-500">Failed to load teacher details.</div>;

  const typeData = Object.entries(data.recordsByType ?? {}).map(([type, count]) => ({
    name: TYPE_LABELS[type] ?? type, count: count as number, fill: TYPE_COLORS[type] ?? "#9ca3af",
  }));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
          <ChevronRight className="h-4 w-4 rotate-180" /> Back to school overview
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-lg font-bold text-primary">{data.teacher?.name?.charAt(0) ?? "?"}</span>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{data.teacher?.name}</h2>
          {data.teacher?.teachingLevel && <p className="text-sm text-gray-500">{data.teacher.teachingLevel}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{data.totalRecords}</p><p className="text-xs text-gray-500 mt-0.5">Total records</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{data.totalStudents}</p><p className="text-xs text-gray-500 mt-0.5">Students</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className={`text-2xl font-bold ${data.openFollowUps > 0 ? "text-red-600" : ""}`}>{data.openFollowUps}</p><p className="text-xs text-gray-500 mt-0.5">Open follow-ups</p></CardContent></Card>
        <Card className={`border ${(data.notEnteredCount ?? 0) > 0 ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${(data.notEnteredCount ?? 0) > 0 ? "text-amber-600" : "text-green-700"}`}>{data.notEnteredCount ?? 0}</p>
            <p className={`text-xs mt-0.5 ${(data.notEnteredCount ?? 0) > 0 ? "text-amber-600" : "text-green-600"}`}>Not in system</p>
          </CardContent>
        </Card>
      </div>
      {typeData.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Records by type</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={typeData} barCategoryGap="30%">
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [v, "Records"]} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>{typeData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      {data.topStudents?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Most documented students</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.topStudents.map((s: any) => (
              <div key={s.studentId} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-gray-700">{s.studentName}</span>
                <Badge variant="secondary" className="text-xs">{s.count} records</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {data.recentRecords?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Recent records</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.recentRecords.map((r: any) => (
              <div key={r.id} className="flex items-start justify-between py-1.5 border-b last:border-0">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-xs border-0" style={{ backgroundColor: TYPE_COLORS[r.type] + "20", color: TYPE_COLORS[r.type] }}>{TYPE_LABELS[r.type] ?? r.type}</Badge>
                    {r.status === "further_action_required" && <Badge className="bg-red-100 text-red-700 border-0 text-xs">Follow-up needed</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{r.studentNames?.join(", ")}</p>
                </div>
                <span className="text-xs text-gray-400 shrink-0 ml-2">{r.date}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Groups Panel ─────────────────────────────────────────────────────────────

function GroupsPanel({ allTeachers, schoolCode }: { allTeachers: any[]; schoolCode: string }) {
  const qc = useQueryClient();
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any | null>(null);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("custom");
  const [newColour, setNewColour] = useState(PALETTE[0]);
  const [memberDraft, setMemberDraft] = useState<string[]>([]);

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["school", "groups"],
    queryFn: fetchGroups,
  });

  const createGroup = useMutation({
    mutationFn: (body: any) =>
      customFetch("/api/school/groups", { method: "POST", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["school", "groups"] }); setShowCreate(false); setNewName(""); },
  });

  const updateGroup = useMutation({
    mutationFn: ({ id, ...body }: any) =>
      customFetch(`/api/school/groups/${id}`, { method: "PUT", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["school", "groups"] }); setEditingGroup(null); },
  });

  const deleteGroup = useMutation({
    mutationFn: (id: number) => customFetch(`/api/school/groups/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["school", "groups"] }); setSelectedGroup(null); },
  });

  const saveMembers = useMutation({
    mutationFn: ({ id, memberIds }: { id: number; memberIds: string[] }) =>
      customFetch(`/api/school/groups/${id}/members`, {
        method: "PUT", body: JSON.stringify({ memberIds }), headers: { "Content-Type": "application/json" },
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["school", "groups"] });
      setSelectedGroup((prev: any) => prev ? { ...prev, memberIds: memberDraft } : null);
    },
  });

  const openGroup = (g: any) => {
    setSelectedGroup(g);
    setMemberDraft(g.memberIds ?? []);
  };

  const toggleMember = (uid: string) =>
    setMemberDraft((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);

  const membersChanged = selectedGroup &&
    JSON.stringify([...memberDraft].sort()) !== JSON.stringify([...(selectedGroup.memberIds ?? [])].sort());

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  // ── Group detail view ──
  if (selectedGroup) {
    const groupTeachers = allTeachers.filter((t) => memberDraft.includes(t.userId));
    const unassignedTeachers = allTeachers.filter((t) => !memberDraft.includes(t.userId));

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setSelectedGroup(null)} className="gap-2 -ml-2">
            <ChevronRight className="h-4 w-4 rotate-180" /> All groups
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => deleteGroup.mutate(selectedGroup.id)}
            disabled={deleteGroup.isPending}
            className="gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete group
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: selectedGroup.colour + "20" }}>
            <div className="h-4 w-4 rounded-full" style={{ backgroundColor: selectedGroup.colour }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selectedGroup.name}</h2>
            <p className="text-sm text-gray-500">{GROUP_TYPES.find(t => t.value === selectedGroup.groupType)?.label ?? "Custom"}</p>
          </div>
        </div>

        {/* Member list */}
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-sm">Members ({groupTeachers.length})</CardTitle>
            {membersChanged && (
              <Button size="sm" onClick={() => saveMembers.mutate({ id: selectedGroup.id, memberIds: memberDraft })} disabled={saveMembers.isPending} className="gap-1.5 h-7 text-xs">
                {saveMembers.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save changes
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-2">
            {groupTeachers.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No teachers in this group yet.</p>
            )}
            {groupTeachers.map((t) => (
              <div key={t.userId} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                    {t.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{t.name}</p>
                    {t.teachingLevel && <p className="text-xs text-gray-400">{t.teachingLevel}</p>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => toggleMember(t.userId)} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Add from unassigned */}
        {unassignedTeachers.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Add to group</CardTitle></CardHeader>
            <CardContent className="p-2">
              {unassignedTeachers.map((t) => (
                <div key={t.userId} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-500">
                      {t.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <p className="text-sm text-gray-700">{t.name}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => toggleMember(t.userId)} className="gap-1 h-7 px-2 text-xs">
                    <Plus className="h-3 w-3" /> Add
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ── Group list view ──
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{(groups as any[]).length} group{(groups as any[]).length !== 1 ? "s" : ""}</p>
        <Button size="sm" onClick={() => { setNewName(""); setNewType("custom"); setNewColour(PALETTE[0]); setShowCreate(true); }} className="gap-1.5" data-write-action>
          <Plus className="h-4 w-4" /> New group
        </Button>
      </div>

      {(groups as any[]).length === 0 && (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <Layers className="h-10 w-10 text-gray-300 mx-auto" />
            <div>
              <p className="font-medium text-gray-600">No groups yet</p>
              <p className="text-sm text-gray-400 mt-1">Create groups to organise your staff by stage, campus, or department.</p>
            </div>
            <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5" data-write-action>
              <Plus className="h-4 w-4" /> Create first group
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(groups as any[]).map((g) => {
          const memberNames = allTeachers.filter((t) => (g.memberIds ?? []).includes(t.userId)).map((t) => t.name);
          return (
            <button
              key={g.id}
              onClick={() => openGroup(g)}
              className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-primary/40 hover:shadow-sm transition-all text-left group bg-white"
            >
              <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: g.colour + "25" }}>
                <div className="h-4 w-4 rounded-full" style={{ backgroundColor: g.colour }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-gray-900 truncate">{g.name}</p>
                  <Badge className="text-xs border-0 bg-gray-100 text-gray-500 shrink-0">{g.memberIds?.length ?? 0}</Badge>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{GROUP_TYPES.find(t => t.value === g.groupType)?.label ?? "Custom"}</p>
                {memberNames.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1.5 truncate">{memberNames.slice(0, 3).join(", ")}{memberNames.length > 3 ? ` +${memberNames.length - 3} more` : ""}</p>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary shrink-0 mt-3" />
            </button>
          );
        })}
      </div>

      {/* Create group dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New group</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Group name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Stage 2, Junior Campus…" className="mt-1.5" autoFocus />
            </div>
            <div>
              <Label>Type</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {GROUP_TYPES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setNewType(t.value)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${newType === t.value ? "border-primary bg-primary/10 text-primary font-medium" : "border-gray-200 text-gray-600 hover:border-primary/40"}`}
                  >{t.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Colour</Label>
              <div className="flex gap-2 mt-1.5 flex-wrap">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    onClick={() => setNewColour(c)}
                    className="h-7 w-7 rounded-full border-2 transition-all"
                    style={{ backgroundColor: c, borderColor: newColour === c ? "#1e293b" : "transparent" }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={() => createGroup.mutate({ name: newName, groupType: newType, colour: newColour })}
              disabled={!newName.trim() || createGroup.isPending}
              className="gap-1.5"
            >
              {createGroup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function SchoolOverview() {
  const { data: profile } = useGetProfile();
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"teachers" | "groups">("teachers");

  const { data, isLoading, error } = useQuery({
    queryKey: ["school", "overview"],
    queryFn: fetchSchoolOverview,
    retry: false,
  });

  const profileData = profile as any;
  const isHeadTeacher = profileData?.schoolRole === "head_teacher";
  const hasSchoolCode = !!profileData?.schoolCode;

  if (!hasSchoolCode || !isHeadTeacher) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">School Overview</h1>
        </div>
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <GraduationCap className="h-12 w-12 text-gray-300 mx-auto" />
            <div>
              <h2 className="text-lg font-semibold text-gray-700 mb-1">Head Teacher access required</h2>
              <p className="text-sm text-gray-500">School Overview is available to verified head teachers only.</p>
              {!hasSchoolCode && <p className="text-sm text-amber-600 mt-2">Go to <strong>Settings → Profile & Region</strong> and enter your school code to get started.</p>}
              {hasSchoolCode && !isHeadTeacher && <p className="text-sm text-amber-600 mt-2">Go to <strong>Settings → Profile & Region</strong> and enter the Head Teacher PIN.</p>}
            </div>
            <Button variant="outline" onClick={() => window.location.href = "/app/settings"}>Go to Settings</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) return <div className="p-8 flex items-center justify-center min-h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (error || !data) {
    const msg = (error as any)?.message ?? "Could not load school overview.";
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">{msg}</p>
            {msg.includes("No school code") && <p className="text-sm text-gray-500 mt-2">Add a school code in <strong>Settings → Profile & Region</strong>.</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (selectedTeacher) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <TeacherDetailPanel teacherUserId={selectedTeacher} onBack={() => setSelectedTeacher(null)} />
      </div>
    );
  }

  const { summary, weeklyActivity, perTeacher } = data;
  const typeData = Object.entries(summary.recordsByType ?? {}).map(([type, count]) => ({
    name: TYPE_LABELS[type] ?? type, count: count as number, fill: TYPE_COLORS[type] ?? "#9ca3af",
  }));
  const weekData = (weeklyActivity ?? []).map((d: any) => ({
    date: format(new Date(d.date), "EEE d"), count: d.count,
  }));

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold text-gray-900">{data.schoolName}</h1>
          </div>
          <p className="text-sm text-gray-500">
            School code: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{data.schoolCode}</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-primary/10 text-primary border-0">Head Teacher View</Badge>
          <ExportCsvButton />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Teachers" value={summary.totalTeachers} color="text-blue-600" />
        <StatCard icon={GraduationCap} label="Students" value={summary.totalStudents} color="text-green-600" />
        <StatCard icon={FileText} label="Total Records" value={summary.totalRecords} color="text-purple-600" />
        <StatCard icon={AlertTriangle} label="Open Follow-ups" value={summary.openFollowUps} color={summary.openFollowUps > 0 ? "text-red-500" : "text-gray-400"} />
      </div>

      {/* Compliance banner */}
      {summary.totalRecords > 0 && (
        <Card className={`border ${summary.notEnteredCount > 0 ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                {summary.notEnteredCount > 0 ? <XCircle className="h-5 w-5 text-amber-500 shrink-0" /> : <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />}
                <div>
                  <p className={`font-semibold text-sm ${summary.notEnteredCount > 0 ? "text-amber-800" : "text-green-800"}`}>
                    {summary.notEnteredCount > 0 ? `${summary.notEnteredCount} record${summary.notEnteredCount !== 1 ? "s" : ""} not yet entered into school system` : "All records entered into school system"}
                  </p>
                  <p className={`text-xs mt-0.5 ${summary.notEnteredCount > 0 ? "text-amber-600" : "text-green-600"}`}>{summary.enteredCount} of {summary.totalRecords} records uploaded</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Progress value={Math.round((summary.enteredCount / summary.totalRecords) * 100)} className="w-24 h-2" />
                <span className={`text-sm font-bold ${summary.notEnteredCount > 0 ? "text-amber-700" : "text-green-700"}`}>{Math.round((summary.enteredCount / summary.totalRecords) * 100)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {weekData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Activity this week</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={weekData} barCategoryGap="30%">
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, "Records"]} /><Bar dataKey="count" fill="#0d9488" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        {typeData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Records by type (school-wide)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={typeData} barCategoryGap="30%">
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, "Records"]} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>{typeData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tab bar: Teachers | Groups */}
      <div className="flex border-b border-gray-200 gap-1">
        {(
          [
            { key: "teachers" as const, label: "All Teachers", icon: Users, count: perTeacher.length },
            { key: "groups" as const, label: "Groups", icon: Layers, count: undefined },
          ] satisfies ReadonlyArray<{
            key: "teachers" | "groups";
            label: string;
            icon: typeof Users;
            count: number | undefined;
          }>
        ).map(({ key, label, icon: Icon, count }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === key ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            <Icon className="h-4 w-4" />
            {label}
            {count !== undefined && <span className="bg-gray-100 text-gray-600 text-xs rounded-full px-1.5 py-0.5">{count}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "teachers" && (
        <Card>
          <CardContent className="p-2">
            {perTeacher.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">
                No other teachers have joined your school code yet.<br />
                Share your school code <code className="bg-gray-100 px-1 rounded">{data.schoolCode}</code> with your staff.
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {perTeacher.map((t: any) => (
                  <TeacherRow key={t.userId} teacher={t} onSelect={() => setSelectedTeacher(t.userId)} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "groups" && (
        <GroupsPanel allTeachers={perTeacher} schoolCode={data.schoolCode} />
      )}
    </div>
  );
}
